<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useSession } from '../auth';
import { trpc } from '../trpc';
import AppLayout from '@/components/layout/AppLayout.vue';
import CurriculaNav from '@/components/layout/CurriculaNav.vue';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-vue-next';

interface Curriculum {
  id: string;
  title: string;
  description: string | null;
  state: string;
  createdAt: string;
  lessonsRemaining: number;
  reviewsDue: number;
}

const router = useRouter();
const session = useSession();
const user = computed(() => session.value?.data?.user);
const isLoading = computed(() => session.value?.isPending);

const curricula = ref<Curriculum[]>([]);
const loading = ref(false);
const error = ref('');

async function fetchCurricula() {
  if (!user.value) return;
  loading.value = true;
  error.value = '';
  try {
    curricula.value = await trpc.listCurriculaWithProgress.query();
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load curricula';
  } finally {
    loading.value = false;
  }
}

function viewCurriculum(curriculum: Curriculum) {
  router.push(`/learn/${curriculum.id}`);
}

function stateLabel(state: string): string {
  const labels: Record<string, string> = {
    pending: 'Pending',
    active: 'Active',
    completed: 'Completed',
    archived: 'Archived',
  };
  return labels[state] || state;
}

onMounted(() => {
  if (!isLoading.value && !user.value) {
    router.push('/sign-in');
  } else {
    fetchCurricula();
  }
});

watch(user, (newUser) => {
  if (newUser) fetchCurricula();
});
</script>

<template>
  <AppLayout>
    <template #nav>
      <CurriculaNav />
    </template>
    <div class="flex-1 overflow-y-auto scrollbar-thin">
      <div class="max-w-4xl mx-auto px-6 py-8">
        <div
          v-if="isLoading"
          class="text-center text-muted-foreground"
        >
          Loading...
        </div>

        <div
          v-else-if="user"
          class="space-y-6"
        >
          <!-- Curricula list -->
          <div class="flex justify-between items-center">
            <h2 class="text-2xl font-semibold">
              Your Curricula
            </h2>
            <Button @click="router.push('/learn/new')">
              <Plus class="size-4" />
              New Curriculum
            </Button>
          </div>

          <div
            v-if="error"
            class="bg-destructive/10 text-destructive p-4 rounded-md"
          >
            {{ error }}
          </div>

          <div
            v-if="loading"
            class="text-center text-muted-foreground"
          >
            Loading curricula...
          </div>

          <div
            v-else-if="curricula.length === 0"
            class="text-center text-muted-foreground py-8"
          >
            No curricula yet. Type a topic above to create your first one!
          </div>

          <div
            v-else
            class="grid gap-4"
          >
            <div
              v-for="curriculum in curricula"
              :key="curriculum.id"
              class="bg-card rounded-lg shadow-md p-4 border border-border cursor-pointer hover:border-primary/50 transition-colors"
              @click="viewCurriculum(curriculum)"
            >
              <div class="flex items-center justify-between gap-3">
                <span class="text-lg font-medium text-foreground">{{ curriculum.title }}</span>
                <span class="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground shrink-0">
                  {{ stateLabel(curriculum.state) }}
                </span>
              </div>
              <p
                v-if="curriculum.description"
                class="text-sm text-muted-foreground mt-1 line-clamp-2"
              >
                {{ curriculum.description }}
              </p>
              <div class="flex items-center justify-between gap-3 mt-3">
                <span class="text-xs text-muted-foreground">
                  {{ curriculum.lessonsRemaining === 0 ? 'Completed' : `${curriculum.lessonsRemaining} ${curriculum.lessonsRemaining === 1 ? 'lesson' : 'lessons'} to go` }}
                </span>
                <div class="flex items-center gap-2">
                  <span
                    v-if="curriculum.reviewsDue > 0"
                    class="text-xs px-2 py-1 rounded-full bg-secondary text-secondary-foreground"
                  >
                    {{ curriculum.reviewsDue }} due
                  </span>
                  <router-link
                    v-if="curriculum.lessonsRemaining > 0"
                    :to="`/learn/${curriculum.id}`"
                    class="text-xs px-2 py-1 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                    @click.stop
                  >
                    Continue
                  </router-link>
                  <span
                    v-else
                    class="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground"
                  >
                    Completed
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
